export interface HyphenationPattern {
  pattern: string;
  exceptions?: string[];
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export class PatternCache {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL: number = 24 * 60 * 60 * 1000;

  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  prune(): number {
    let pruned = 0;
    const now = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
        pruned++;
      }
    }

    return pruned;
  }
}

export const patternCache = new PatternCache();

export function getCachedPatterns(language: string): string[] | null {
  return patternCache.get<string[]>(`hyphenation.${language}`);
}

export function setCachedPatterns(language: string, patterns: string[], ttl?: number): void {
  patternCache.set(`hyphenation.${language}`, patterns, ttl);
}

export function hasCachedPatterns(language: string): boolean {
  return patternCache.has(`hyphenation.${language}`);
}

export function clearPatternCache(): void {
  patternCache.clear();
}

export function getPatternCacheStats(): { size: number; keys: string[] } {
  return {
    size: patternCache.size(),
    keys: patternCache.keys(),
  };
}
